import React, { useMemo } from "react";
import { Grid } from "@mui/material";

import type { LivingAirTemperatureSensor, Sensor } from "@core/apis/shared/types/sensors.model";

import IndoorClimateKpiCards from "./IndoorClimateKpiCards";
import SensorSelectionPanel from "./SensorSelectionPanel";
import { useSensorOverviewData } from "./useSensorOverviewData";

export const AVERAGE_SENSOR_UID = "average-all-sensor";

export type { SensorReading, Building } from "./useSensorOverviewData";

function isLivingAirSensor(sensor: Sensor): sensor is LivingAirTemperatureSensor {
  return sensor.sensor_type_technical_name === "living_air_temperature";
}

type SensorOverviewProps = {
  sensors: Sensor[];
  fullSensors: Sensor[];
  selectedSensors?: string[];
  avgSensor: Sensor | null;
  setSelectedSensors: (uids: string[]) => void;
  isLoading?: boolean;
  onSensorSelect?: (sensorId: string) => void;
  targetTemperature?: number | null;
};

const SensorOverview: React.FC<SensorOverviewProps> = ({
  sensors,
  fullSensors,
  selectedSensors,
  avgSensor,
  setSelectedSensors,
  isLoading,
  onSensorSelect,
  targetTemperature,
}) => {
  const livingAirSensors = useMemo(() => sensors.filter(isLivingAirSensor), [sensors]);
  const livingAirAvgSensor = avgSensor && isLivingAirSensor(avgSensor) ? avgSensor : null;

  const {
    buildings,
    allFloors,
    avgTemp,
    avgTimestamp,
    humidityStatus,
    activeSensors,
    totalSensors,
  } = useSensorOverviewData(livingAirSensors, livingAirAvgSensor);

  return (
    <Grid container spacing={2} flexDirection="column">
      <Grid item xs={12}>
        <IndoorClimateKpiCards
          avgTemp={avgTemp}
          avgTimestamp={avgTimestamp}
          humidityStatus={humidityStatus}
        />
      </Grid>
      <Grid item xs={12}>
        <SensorSelectionPanel
          buildings={buildings}
          allFloors={allFloors}
          activeSensors={activeSensors}
          totalSensors={totalSensors}
          fullSensors={fullSensors}
          selectedSensors={selectedSensors}
          setSelectedSensors={setSelectedSensors}
          onSensorSelect={onSensorSelect}
          targetTemperature={targetTemperature}
          isLoading={isLoading}
        />
      </Grid>
    </Grid>
  );
};

export default SensorOverview;
